/**
 * Nijhof Brothers: kleine UI-helpers voor de NB-secties.
 * - .nb-reveal: subtiele fade-in zodra een blok in beeld komt
 * - form[data-nb-form="afspraak"]: datumveld minimaal vandaag, optionele webhook-verzending
 * - [data-nb-scroll]: soepel scrollen naar een anker (bijv. de configurator)
 * - [data-nb-bezorgkeuze]: bezorgen of afhalen in het Exclusives-koopblok
 * - [data-nb-stofkeuze]: stofkeuze (uitklapmenu met stalen) in het Exclusives-koopblok
 */
function reveal() {
  const items = document.querySelectorAll('.nb-reveal:not(.is-in)');
  if (!items.length) return;
  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  items.forEach((el) => io.observe(el));
  // Vangnet: als de observer niet vuurt (verborgen tabblad, printen, oude browser) toch alles tonen.
  setTimeout(() => {
    document.querySelectorAll('.nb-reveal:not(.is-in)').forEach((el) => el.classList.add('is-in'));
  }, 2500);
}

function afspraakForms() {
  document.querySelectorAll('form[data-nb-form="afspraak"]').forEach((form) => {
    const date = form.querySelector('input[type="date"]');
    if (date && !date.min) date.min = new Date().toISOString().slice(0, 10);

    const endpoint = form.dataset.nbEndpoint;
    if (!endpoint) return; // standaard: Shopify-contactformulier, native post

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const status = form.querySelector('[data-nb-status]');
      const data = Object.fromEntries(new FormData(form).entries());
      if (button) button.disabled = true;
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'showroom_afspraak', bron: 'webshop', url: location.href, ...data }),
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const fields = form.querySelector('[data-nb-fields]');
        if (fields) fields.hidden = true;
        const ok = form.querySelector('[data-nb-success]');
        if (ok) ok.hidden = false;
        if (window.NB) window.NB.track('appointment_request_received', { kanaal: 'webhook' });
      } catch (err) {
        if (status) {
          status.hidden = false;
          status.textContent = 'Versturen lukte niet. Probeer het opnieuw of stuur ons een WhatsApp.';
        }
        if (button) button.disabled = false;
      }
    });
  });
}

function smoothScroll() {
  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target.closest('[data-nb-scroll]') : null;
    if (!el) return;
    const selector = el.dataset.nbScroll || el.getAttribute('href');
    const target = selector ? document.querySelector(selector) : null;
    if (!target) return;
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    const focusable = target.querySelector('input, button, select, [tabindex]');
    if (focusable) focusable.focus({ preventScroll: true });
  });
}

/**
 * Google-score bijwerken zodra de Google Reviews-app (Trustify) haar gegevens op de pagina zet:
 * window.trggGooglePlace = { rating, reviews_number, ... }.
 * De app synchroniseert niet altijd; daarom wint het hoogste aantal (ingesteld of live), want
 * het aantal reviews kan alleen groeien. Zonder app-gegevens blijven de ingestelde waarden staan.
 */
function googleScore() {
  if (!document.querySelector('[data-nb-rating]')) return;
  let tries = 0;
  const timer = setInterval(() => {
    tries += 1;
    const p = window.trggGooglePlace || null;
    const rating = p && Number(p.rating);
    const count = p && Number(p.reviews_number || p.user_ratings_total || 0);
    if (rating) {
      const txt = rating.toFixed(1).replace('.', ',');
      document.querySelectorAll('[data-nb-rating]').forEach((el) => {
        el.textContent = txt;
      });
      if (count) {
        document.querySelectorAll('[data-nb-count]').forEach((el) => {
          const huidig = Number(String(el.textContent).replace(/\D/g, '')) || 0;
          el.textContent = String(Math.max(huidig, count));
        });
      }
      clearInterval(timer);
    } else if (tries > 20) {
      clearInterval(timer);
    }
  }, 500);
}

/**
 * Stofswatches op de variant-pills van Horizon. Shopify maakt alleen automatische swatches voor
 * zijn eigen kleur-taxonomie, niet voor eigen metaobjecten (NB Stof). Het koopblok zet daarom een
 * JSON-map {optiewaarde: {color, image}} op de pagina; hier krijgt elke bijpassende pill een rondje.
 * Loopt opnieuw na elke variantwissel, omdat Horizon de sectie dan opnieuw rendert.
 */
function stofSwatches() {
  function decorate() {
    const data = document.querySelector('[data-nb-stof-swatches]');
    if (!data) return;
    let map;
    try {
      map = JSON.parse(data.textContent);
    } catch {
      return;
    }
    document.querySelectorAll('.variant-option__button-label').forEach((label) => {
      if (label.querySelector('.nb-swatch-dot')) return;
      const input = label.querySelector('input[type="radio"]');
      const entry = input && map[input.value];
      if (!entry || (!entry.color && !entry.image)) return;
      const dot = document.createElement('span');
      dot.className = 'nb-swatch-dot';
      dot.setAttribute('aria-hidden', 'true');
      if (entry.image) dot.style.backgroundImage = 'url(' + entry.image + ')';
      else dot.style.backgroundColor = entry.color;
      const text = label.querySelector('.variant-option__button-label__text');
      if (text) text.insertAdjacentElement('beforebegin', dot);
      else label.appendChild(dot);
      label.classList.add('nb-has-swatch');
    });
  }
  decorate();
  document.addEventListener('variant:update', () => {
    decorate();
    requestAnimationFrame(decorate);
    setTimeout(decorate, 300);
  });
  document.addEventListener('shopify:section:load', decorate);
}

/**
 * Het vooringevulde WhatsApp-bericht in het Exclusives-koopblok noemt de gekozen uitvoering
 * (bijv. "chaise longue rechts"). Horizon rendert de knop niet opnieuw bij een variantwissel,
 * dus hier wordt de uitvoering in de link vervangen door de nieuw gekozen variant. De optie op
 * positie data-nb-optie-weg (impregneren) blijft buiten het bericht.
 */
function waUitvoering() {
  document.addEventListener('variant:update', (event) => {
    const variant = event.detail && event.detail.resource;
    if (!variant || !variant.title) return;
    document.querySelectorAll('a[data-nb-uitvoering]').forEach((link) => {
      const weg = link.dataset.nbOptieWeg;
      const nieuw =
        weg !== undefined && Array.isArray(variant.options)
          ? variant.options.filter((_, i) => String(i) !== weg).join(' / ')
          : String(variant.title);
      const oud = link.dataset.nbUitvoering;
      if (!oud || oud === nieuw) return;
      let url;
      try {
        url = new URL(link.href);
      } catch {
        return;
      }
      const tekst = url.searchParams.get('text');
      if (!tekst) return;
      url.searchParams.set('text', tekst.replace('(' + oud + ')', '(' + nieuw + ')'));
      link.href = url.toString();
      link.dataset.nbUitvoering = nieuw;
    });
  });
}

/**
 * Bezorgkeuze in het Exclusives-koopblok (blocks/_nb-bezorgkeuze.liquid). Bij een bezorgoptie
 * verschijnt "Type woning" in de gekozen kaart; bij afhalen verdwijnt het en gaat het niet mee in
 * de bestelling. De keuze komt onderaan het vooringevulde bericht van de WhatsApp-hoofdknop.
 * Na een variantwissel wordt de keuze hersteld, voor het geval Horizon het koopblok opnieuw rendert.
 */
function bezorgkeuze() {
  const ZIN = /\n\nBezorging: [^\n]*$/;
  const TOKEN = / \[[^\]]*\]\s*$/;
  const staat = { methode: null, woning: '', metWoning: false };

  function waBijwerken() {
    let zin = '';
    if (staat.methode) {
      zin = '\n\nBezorging: ' + staat.methode;
      if (staat.metWoning && staat.woning) zin += ', ' + staat.woning.toLowerCase();
    }
    document.querySelectorAll('.nb-ex-koop a.nb-wa').forEach((link) => {
      let url;
      try {
        url = new URL(link.href);
      } catch {
        return;
      }
      const tekst = url.searchParams.get('text');
      if (tekst === null) return;
      // Het Growth OS-token (" [NBX-… • abc123]", gezet bij een eerdere klik) blijft achteraan staan
      const token = (tekst.match(TOKEN) || [''])[0];
      url.searchParams.set('text', tekst.replace(TOKEN, '').replace(ZIN, '') + zin + token);
      link.href = url.toString();
    });
  }

  function toonWoning(root) {
    const veld = root.querySelector('[data-nb-woning-veld]');
    if (!veld) return;
    const gekozen = root.querySelector('input[name="properties[Bezorging]"]:checked');
    staat.metWoning = Boolean(gekozen && gekozen.hasAttribute('data-nb-woning'));
    if (staat.metWoning) gekozen.closest('.nb-bk__optie').appendChild(veld);
    veld.hidden = !staat.metWoning;
    const select = veld.querySelector('select');
    if (select) select.disabled = !staat.metWoning;
  }

  function herstel() {
    document.querySelectorAll('[data-nb-bezorgkeuze]').forEach((root) => {
      const radio = Array.from(root.querySelectorAll('input[name="properties[Bezorging]"]')).find(
        (r) => r.value === staat.methode
      );
      if (radio) radio.checked = true;
      const select = root.querySelector('[data-nb-woning-veld] select');
      if (select && staat.woning) select.value = staat.woning;
      toonWoning(root);
    });
    waBijwerken();
  }

  document.addEventListener('change', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    const root = el && el.closest('[data-nb-bezorgkeuze]');
    if (!root) return;
    if (el.matches('input[name="properties[Bezorging]"]')) {
      staat.methode = el.value;
      toonWoning(root);
    } else if (el.matches('select')) {
      staat.woning = el.value;
    } else {
      return;
    }
    waBijwerken();
    totaalBijwerken(null);
    if (window.NB) {
      window.NB.track('delivery_option_selected', {
        methode: staat.methode,
        woning: (staat.metWoning && staat.woning) || null,
      });
    }
  });

  document.addEventListener('variant:update', () => {
    if (!staat.methode) return;
    requestAnimationFrame(herstel);
    setTimeout(herstel, 300);
  });
}

/**
 * Stofkeuze in het Exclusives-koopblok (blocks/_nb-stofkeuze.liquid). Een staal klikt de verborgen
 * Horizon-knop van de optie aan, zodat variant, URL en bestelformulier via Horizon lopen. Hier: menu
 * open/dicht, grote stoffoto en meerprijs, staalkaart op de hoofdfoto (alleen bij een andere stof dan
 * de standaard), regel onder de foto's en de prijs in het koopblok (Horizon werkt die niet zelf bij).
 */
function prijsTekst(cent) {
  const euro = cent / 100;
  if (cent % 100 === 0) return '€' + euro.toLocaleString('nl-NL') + ',-';
  return '€' + euro.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Totaalprijs boven "Bestel direct" (blocks/buy-buttons.liquid, [data-nb-totaal]): prijs van de gekozen
 * variant plus de gekozen bezorging (data-nb-cent op de bezorgkeuze), en de keuze in woorden (opstelling
 * en stof; impregneren en bezorging pas als de klant die zelf koos).
 * @param {{ price?: number, options?: string[] } | null} variant
 */
function totaalBijwerken(variant) {
  const bezorging = document.querySelector('[data-nb-bezorgkeuze] input[name="properties[Bezorging]"]:checked');
  document.querySelectorAll('[data-nb-totaal]').forEach((box) => {
    if (variant && typeof variant.price === 'number') box.dataset.nbBasisCent = String(variant.price);
    const basis = Number(box.dataset.nbBasisCent);
    const prijs = box.querySelector('[data-nb-totaal-prijs]');
    if (prijs && Number.isFinite(basis)) {
      const nieuw = prijsTekst(basis + (bezorging ? Number(bezorging.dataset.nbCent) || 0 : 0));
      if (prijs.textContent.trim() !== nieuw) {
        prijs.textContent = nieuw;
        prijs.classList.add('nb-prijs--pop');
        setTimeout(() => prijs.classList.remove('nb-prijs--pop'), 260);
      }
    }
    const keuze = box.querySelector('[data-nb-totaal-keuze]');
    if (!keuze) return;
    const weg = box.dataset.nbOptieWeg;
    let tekst = keuze.dataset.basis || keuze.textContent;
    if (variant && Array.isArray(variant.options)) {
      tekst = variant.options.filter((_, i) => String(i) !== weg).join(', ');
    }
    keuze.dataset.basis = tekst;
    const imp = document.querySelector('[data-nb-impregneerkeuze] input[name="nb_impregneren"]:checked');
    let regel = imp ? tekst + ', ' + imp.value.toLowerCase() : tekst;
    // Kleine letter midden in de zin, behalve bij een afkorting zoals "VIP-bezorging"
    if (bezorging) regel += ', ' + bezorging.value.replace(/^[A-Z](?![A-Z])/, (c) => c.toLowerCase());
    keuze.textContent = regel;
  });
}

function stofkeuze() {
  let open = false;

  function fotoExtra(root, knop) {
    const media = document.querySelector('.product-information__media');
    if (!media) return;
    if (root.dataset.noot && !media.querySelector('.nb-sk-noot')) {
      const noot = document.createElement('p');
      noot.className = 'nb-sk-noot';
      noot.textContent = root.dataset.noot;
      media.appendChild(noot);
    }
    const vlak = media.querySelector('slideshow-container') || media;
    let ov = vlak.querySelector('.nb-sk-ov');
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'nb-sk-ov';
      ov.setAttribute('aria-hidden', 'true');
      ov.innerHTML = '<img alt="" width="96" height="96"><span><strong></strong><small></small></span>';
      if (getComputedStyle(vlak).position === 'static') vlak.style.position = 'relative';
      vlak.appendChild(ov);
    }
    const standaard = !knop || knop.dataset.waarde === root.dataset.standaard || !knop.dataset.mini;
    ov.hidden = standaard;
    if (standaard) return;
    ov.querySelector('img').src = knop.dataset.mini;
    ov.querySelector('strong').textContent = knop.dataset.waarde;
    ov.querySelector('small').textContent = root.dataset.ovLabel || '';
  }

  function zetKeuze(root, knop) {
    root.querySelectorAll('.nb-sk__staal').forEach((k) => k.setAttribute('aria-checked', String(k === knop)));
    root.querySelectorAll('[data-nb-sk-naam]').forEach((el) => (el.textContent = knop.dataset.waarde));
    root.querySelectorAll('[data-nb-sk-meer]').forEach((el) => (el.textContent = knop.dataset.meer));
    const groot = root.querySelector('[data-nb-sk-groot]');
    if (groot && knop.dataset.groot) groot.src = knop.dataset.groot;
    const mini = root.querySelector('[data-nb-sk-mini]');
    if (mini && knop.dataset.mini) mini.src = knop.dataset.mini;
    fotoExtra(root, knop);
  }

  function sync() {
    const root = document.querySelector('[data-nb-stofkeuze]');
    if (!root) return;
    const knop = root.querySelector('.nb-sk__staal[aria-checked="true"]') || root.querySelector('.nb-sk__staal');
    if (knop) zetKeuze(root, knop);
    const kop = root.querySelector('.nb-sk__kop');
    const paneel = root.querySelector('.nb-sk__paneel');
    if (kop && paneel) {
      paneel.hidden = !open;
      kop.setAttribute('aria-expanded', String(open));
    }
  }

  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    if (el.closest('.nb-sk__kop')) {
      open = !open;
      sync();
      return;
    }
    const knop = el.closest('.nb-sk__staal');
    if (!knop) return;
    const root = knop.closest('[data-nb-stofkeuze]');
    zetKeuze(root, knop);
    const inputs = document.querySelectorAll(
      'variant-picker fieldset[data-option-id$="-' + root.dataset.optie + '"] input[type="radio"]'
    );
    const input = Array.from(inputs).find((i) => i.value === knop.dataset.waarde);
    if (input && !input.checked) input.click();
  });

  document.addEventListener('variant:update', (event) => {
    const variant = event.detail && event.detail.resource;
    if (variant && typeof variant.price === 'number') {
      document.querySelectorAll('.nb-ex-koop .nb-prijs').forEach((p) => {
        const nieuw = prijsTekst(variant.price);
        if (p.textContent.trim() === nieuw) return;
        p.textContent = nieuw;
        p.classList.add('nb-prijs--pop');
        setTimeout(() => p.classList.remove('nb-prijs--pop'), 260);
      });
      document.querySelectorAll('[data-nb-sk-totaal]').forEach((el) => (el.textContent = prijsTekst(variant.price)));
    }
    totaalBijwerken(variant || null);
    requestAnimationFrame(sync);
    setTimeout(sync, 300);
  });

  sync();
}

/**
 * Impregneerkeuze in het Exclusives-koopblok (blocks/_nb-impregneerkeuze.liquid). Een keuze klikt de
 * verborgen Horizon-knop van de optie aan, zodat variant, prijs en bestelformulier via Horizon lopen
 * (de prijs in het koopblok werkt stofkeuze() bij). Na een variantwissel wordt de keuze hersteld,
 * voor het geval Horizon het koopblok opnieuw rendert.
 */
function impregneerkeuze() {
  let gekozen = null;

  function herstel() {
    document.querySelectorAll('[data-nb-impregneerkeuze] input[name="nb_impregneren"]').forEach((r) => {
      r.checked = r.value === gekozen;
    });
  }

  document.addEventListener('change', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el || !el.matches('[data-nb-impregneerkeuze] input[name="nb_impregneren"]')) return;
    gekozen = el.value;
    const optie = el.closest('[data-nb-impregneerkeuze]').dataset.optie;
    const inputs = document.querySelectorAll(
      'variant-picker fieldset[data-option-id$="-' + optie + '"] input[type="radio"]'
    );
    const input = Array.from(inputs).find((i) => i.value === gekozen);
    if (input && !input.checked) input.click();
    totaalBijwerken(null);
    if (window.NB) window.NB.track('impregnation_selected', { keuze: gekozen });
  });

  document.addEventListener('variant:update', () => {
    if (!gekozen) return;
    requestAnimationFrame(herstel);
    setTimeout(herstel, 300);
  });
}

/**
 * Achtergrondvideo's (video[data-nb-src]) pas laden als het blok in beeld komt, en alleen op
 * desktop zonder "verminder beweging" of databesparing. Op mobiel blijft alleen de stilstaande
 * achtergrond staan: dat scheelt bijna 3 MB per paginaweergave.
 */
function lazyVideos() {
  const videos = document.querySelectorAll('video[data-nb-src]');
  if (!videos.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobiel = window.matchMedia('(max-width: 749px)').matches;
  const saveData = navigator.connection && navigator.connection.saveData;
  if (reduce || mobiel || saveData || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const video = entry.target;
        if (!video.getAttribute('src')) {
          video.setAttribute('src', video.dataset.nbSrc);
          video.load();
        }
        video.addEventListener('playing', () => video.classList.add('is-ready'), { once: true });
        video.play().catch(() => {});
        io.unobserve(video);
      });
    },
    { rootMargin: '200px 0px' }
  );
  videos.forEach((video) => io.observe(video));
}

function init() {
  reveal();
  afspraakForms();
  smoothScroll();
  googleScore();
  stofSwatches();
  waUitvoering();
  bezorgkeuze();
  stofkeuze();
  impregneerkeuze();
  lazyVideos();
  document.addEventListener('shopify:section:load', reveal);
  document.dispatchEvent(new CustomEvent('nb:ready'));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
